import { useMemo, useState, type KeyboardEvent } from 'react';
// EAA-1826: Popover straight from antd, matching `roleMap/AddRoleCombobox`'s
// precedent — the UI-kit barrel does not re-export it yet.
import { Popover } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Listbox, type ListboxItem } from '@/components/ui';
import { useActingOrg } from '@/auth/actingOrgContext';
import type { MeAdminOrg } from '@/api/orgAdmin';

/** UX-SPEC §4: "Search appears when the user has > 7 orgs." */
const SEARCH_THRESHOLD = 7;

/**
 * EAA-1797 (P4b): the console's acting-organization selector. Renders only
 * once at least one org is resolved — `useActingOrg()` returns `null` outside
 * the console (no provider mounted there).
 *
 * EAA-1813 (canonical shell — handoff frame 09): the sidebar identity card
 * (tile + name + caption [+ chevron]). `actingOrgContext`, its sessionStorage
 * persistence and its query-invalidation on switch are UNTOUCHED — presentation
 * only.
 *
 * EAA-1826: the menu is a real popover (frame 23/24), not a transparent native
 * `<select>` laid over the card. The old note here claimed "no dropdown
 * dependency — established design-pass rule"; that rule is WRONG for components
 * and `docs/design/COMPONENTS-registry.md` says so at the top — every registry
 * entry names the antd primitive to build from, and "Native Select" is scoped to
 * "where a native control is required (mobile, print forms)". A sidebar switcher
 * is neither, and the native control rendered the OS menu: square corners, OS
 * highlight, none of the popover's chrome, and no room for the search box, the
 * per-row identity or the footnote the spec requires.
 *
 * UX-SPEC §4: "A user with exactly one org sees the name as static text, not
 * a button." A caller who administers only one org (`canSwitch === false`) gets
 * the same card with no trigger and no chevron — genuinely inert, not a
 * disabled-looking control that still reads as clickable.
 */
export interface ActingOrgPickerProps {
    /**
     * EAA-1843: `compact` is the single-line pill the permissions page header
     * draws (frame 04) — tile, name, chevron, 34px tall, no caption.
     *
     * It is the SAME control, not a second one: both variants read and write the
     * one `actingOrgContext`, so switching in either place moves the whole
     * console. Frame 04 shows it in the page header because that frame draws the
     * sidebar collapsed to its icon rail, where the card variant has nowhere to
     * live; on a screen scoped to one org, having the scope visible next to the
     * title is the point.
     *
     * EAA-1846 (frame 95): `rail` is the third — tile only, no name/caption/
     * chevron at all, sized for the 72px collapsed rail itself (frame 95's own
     * collapsed rail keeps a compact identity badge rather than hiding it
     * outright, which `ConsoleNav`'s rail-mode CSS previously did). The org
     * name moves into a `title` tooltip, the same convention every other
     * rail icon already uses when its label disappears.
     */
    variant?: 'card' | 'compact' | 'rail';
}

export function ActingOrgPicker({ variant = 'card' }: ActingOrgPickerProps = {}) {
    const { t } = useTranslation();
    const acting = useActingOrg();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    // Memoised because the `?? []` fallback would otherwise mint a new array on
    // every render, making the filter below recompute for nothing.
    const organizations = useMemo(() => acting?.organizations ?? [], [acting?.organizations]);
    const showSearch = organizations.length > SEARCH_THRESHOLD;

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return organizations;
        return organizations.filter(
            (org) =>
                org.name.toLowerCase().includes(q) || org.code.toLowerCase().includes(q)
        );
    }, [organizations, query]);

    /**
     * EAA-1839: the option rows now come from the kit's `Listbox`, so this maps
     * the filtered orgs onto its item shape. `useMemo` is above the guard below
     * because hooks must not sit behind an early return.
     */
    const items = useMemo<ListboxItem<MeAdminOrg>[]>(
        () => filtered.map((org) => ({ id: org.id, data: org })),
        [filtered]
    );

    if (!acting || !acting.actingOrg) {
        return null;
    }

    const { actingOrg, canSwitch, setActingOrg, actingOrgId } = acting;
    const initials = getOrgInitials(actingOrg.name);
    const compact = variant === 'compact';
    const rail = variant === 'rail';
    const base = compact
        ? 'portal-console-org-picker portal-console-org-picker--compact'
        : rail
          ? 'portal-console-org-picker portal-console-org-picker--rail'
          : 'portal-console-org-picker';
    // Rail mode has no room for a name/caption/chevron — the tooltip carries
    // what those would have said, same as every other collapsed-rail icon.
    const railTitle = `${actingOrg.name} — ${t('shell.orgPicker.currentOrgLabel')}`;

    const card = rail ? (
        <span className="portal-console-org-picker-tile" aria-hidden="true">
            {initials}
        </span>
    ) : (
        <>
            <span className="portal-console-org-picker-tile" aria-hidden="true">
                {initials}
            </span>
            <span className="portal-console-org-picker-body">
                <span className="portal-console-org-picker-name">{actingOrg.name}</span>
                {!compact && (
                    <span className="portal-console-org-picker-caption">
                        {t('shell.orgPicker.currentOrgLabel')}
                    </span>
                )}
            </span>
        </>
    );

    if (!canSwitch) {
        return (
            <div className={base} title={rail ? railTitle : t('shell.orgPicker.currentOrgLabel')}>
                {card}
            </div>
        );
    }

    function handleOpenChange(next: boolean) {
        setOpen(next);
        // EAA-1839: the search box now focuses itself via `autoFocus` on the kit
        // `Input` (the same conversion RolePickerPopover made under EAA-1817),
        // so the ref + setTimeout dance this used to need is gone. The popover
        // content mounts on open, so the attribute fires at the right moment.
        if (next) setQuery('');
    }

    function choose(orgId: string) {
        // Re-picking the current org is a no-op write: `setActingOrg` invalidates
        // every console query, so firing it would reload the whole console to
        // arrive exactly where it already is.
        if (orgId !== actingOrgId) setActingOrg(orgId);
        setOpen(false);
    }

    function onPanelKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            event.preventDefault();
            setOpen(false);
        }
    }

    const panel = (
        <div className="portal-org-switch" onKeyDown={onPanelKeyDown}>
            <p className="portal-org-switch__label">{t('shell.orgPicker.currentOrgLabel')}</p>

            {showSearch && (
                <label className="portal-org-switch__search">
                    <svg
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    >
                        <circle cx="11" cy="11" r="7" />
                        <path d="m20 20-3.5-3.5" strokeLinecap="round" />
                    </svg>
                    {/* EAA-1839: raw <input> → kit Input. This file imported no
                        kit component at all, so the search box was a native
                        field styled by `.portal-org-switch__search input` — the
                        exact fallback the barrel's Button/Input/Checkbox comment
                        records. That rule still lands the borderless-inside-a-
                        bordered-label look, so the chrome is unchanged. */}
                    <Input
                        autoFocus
                        variant="borderless"
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder={t('shell.orgPicker.searchPlaceholder')}
                        aria-label={t('shell.orgPicker.searchPlaceholder')}
                    />
                </label>
            )}

            {/* EAA-1839: the hand-rolled `role="listbox"` / `role="option"` pair is
                now the kit's `Listbox`, which also brings the arrow/Home/End/Enter
                nav this panel never had. `optionsUnstyled` leaves the row chrome to
                `.portal-org-switch__row`: the frame's 6%-of-primary "is-current"
                tint and the sunken hover are CSS, and the kit's inline option
                palette would have overridden both with a flat neutral. */}
            <Listbox<MeAdminOrg>
                className="portal-org-switch__list"
                aria-label={t('shell.orgPicker.listAriaLabel')}
                items={items}
                selectedId={actingOrgId}
                onSelect={(item) => choose(item.id)}
                optionsUnstyled
                optionClassName={(_item, state) =>
                    'portal-org-switch__row' + (state.selected ? ' is-current' : '')
                }
                emptyState={
                    <p className="portal-org-switch__empty">{t('shell.orgPicker.emptyState')}</p>
                }
                renderItem={(item, state) => (
                    <>
                        <span className="portal-org-switch__tile" aria-hidden="true">
                            {getOrgInitials(item.data.name)}
                        </span>
                        <span className="portal-org-switch__body">
                            <span className="portal-org-switch__name">{item.data.name}</span>
                            {/* The frame shows the roles held in each org
                                ("org_admin, teacher"). `/me/admin` returns only
                                {id, code, name} — see `MeAdminOrg` — so there is
                                no role data to render here. The code is shown
                                instead (the same identity line the sheet's org
                                hover card uses); it is a real field, where a
                                role list would be invented. */}
                            <span className="portal-org-switch__code">{item.data.code}</span>
                        </span>
                        {state.selected && (
                            <svg
                                className="portal-org-switch__check"
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={2.2}
                                aria-hidden="true"
                            >
                                <path
                                    d="m5 13 4 4 10-10"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        )}
                    </>
                )}
            />

            {/* UX-SPEC §4: required copy, not a nicety — switching re-scopes every
                console query, so the operator is told before they act. */}
            <p className="portal-org-switch__note">{t('shell.orgPicker.switchNote')}</p>

            <Link
                className="portal-org-switch__link"
                to="/profile"
                onClick={() => setOpen(false)}
            >
                {t('shell.orgPicker.viewAllPermissionsLink')} <span aria-hidden="true">→</span>
            </Link>
        </div>
    );

    return (
        <Popover
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
            content={panel}
            placement={compact ? 'bottomLeft' : 'rightTop'}
            overlayInnerStyle={{ padding: 0 }}
        >
            <div
                className={`${base} portal-console-org-picker--switchable`}
                role="button"
                tabIndex={0}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={rail ? railTitle : t('shell.orgPicker.currentOrgLabel')}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleOpenChange(true);
                    }
                }}
            >
                {card}
                {!rail && (
                    <svg
                        className="portal-console-org-picker-chevron"
                        viewBox="0 0 24 24"
                        width="15"
                        height="15"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        aria-hidden="true"
                    >
                        {/* Compact sits under a title, where a single "opens downward"
                            caret matches the frame; the sidebar card keeps the two-way
                            glyph that reads as "switch". */}
                        <path
                            d={compact ? 'm6 9 6 6 6-6' : 'm8 9 4-4 4 4M8 15l4 4 4-4'}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </div>
        </Popover>
    );
}

/**
 * EAA-1813: first letter of up to the first two words, uppercased — a
 * generic identity-tile initial for an arbitrary org name. The handoff's own
 * tile literally spells "EMG" (a hardcoded brand mark for that one seed org),
 * which cannot generalise to every org this switcher may show, so this
 * derives something legible from whatever `name` the API returns instead.
 */
function getOrgInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}
